

import { useState, useCallback, useMemo, useEffect } from "react"
import {
  Trash2,
  Search,
  FileArchive,
  RefreshCw,
  Send,
  FolderOpen,
  Settings,
  DollarSign,
  ClipboardCheck,
  Lock,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Inbox,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import ButtonConfirm from "@/components/ui/button-confirm"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { StepResult } from "./step-result"
import { ZipUpload } from "./zip-upload"
import { AttemptHistory } from "./attempt-history"
import type { StepResult as StepResultType, WorkflowStep, Attempt, Resumo } from "@/types/processador-previdenciario"
import { cn } from "@/lib/utils"
import {  readStoredA1Certificate } from "../certificado-upload"
import { useToast } from "@/lib/hooks/use-toast"
import {
  useCreatePrevidenciario,
  useEnviarPrevidenciarioZip,
  useFetchPrevidenciarioDetalhesById,
  useGerarXmlCorrigido,
  useReenviarPrevidenciarioZip,
  useDownloadPrevidenciarioZip,
  useFiltrarS3000,
} from "@/features/products/services"
import { isDownloadFileResult, triggerDownloadFile } from "@/features/products/services/download-utils"
import type {
  PrevidenciarioHistoricoResponse,
  PrevidenciarioWorkflowResponse,
} from "@/features/products/services/useFetchPrevidenciarioDetalhesById"

interface StepConfig {
  id: WorkflowStep
  label: string
  shortLabel: string
  icon: React.ComponentType<{ className?: string }>
  description: string
  group?: "analysis_actions"
  acceptsZip?: boolean
  acceptsZipOnRetry?: boolean
}

interface AnalysisDownloadAction {
  id: "baixar_zip"
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
}

interface AnalysisFileOption {
  value: string
  label: string
}

const ANALYSIS_DOWNLOAD_ACTIONS: AnalysisDownloadAction[] = [
  {
    id: "baixar_zip",
    label: "Baixar ZIP",
    description: "Baixa os eventos selecionados em um pacote ZIP.",
    icon: FileArchive,
  },
]

const ANALYSIS_FILE_OPTIONS: AnalysisFileOption[] = [
  { value: "planilha", label: "Planilha" },
  { value: "global", label: "Geral" },
  { value: "s1200-s2299", label: "Verbas" },
  { value: "s1210", label: "Pagamento" },
  { value: "s3000", label: "Exclusões" },
  { value: "s1298", label: "Abertura" },
  { value: "s1010", label: "Rubricas" },
  { value: "s1299", label: "Fechamento" }
]

const STEPS: StepConfig[] = [
  {
    id: "limpar_xmls",
    label: "Limpar XMLs",
    shortLabel: "Limpar",
    icon: Trash2,
    description: "Remove os XMLs antigos do diretorio de processamento",
    acceptsZip: true,
  },
  {
    id: "fazer_analise",
    label: "Fazer Analise",
    shortLabel: "Analise",
    icon: Search,
    description: "Analisa as inconsistencias e oportunidades de ajuste",
    acceptsZip: true,
  },
  {
    id: "enviar_rubricas",
    label: "Enviar Rubricas",
    shortLabel: "Rubricas",
    icon: Send,
    description: "Envia as rubricas para o eSocial",
  },
  {
    id: "abrir_folha",
    label: "Abrir Folha",
    shortLabel: "Abrir",
    icon: FolderOpen,
    description: "Abre a folha de pagamento do periodo",
    acceptsZipOnRetry: true,
  },
  {
    id: "preparar_ambiente",
    label: "Preparar Ambiente",
    shortLabel: "Ambiente",
    icon: Settings,
    description: "Configura o ambiente para processamento da folha",
    acceptsZipOnRetry: true,
  },
  {
    id: "atualizar_verbas",
    label: "Atualizar Verbas",
    shortLabel: "Verbas",
    icon: DollarSign,
    description: "Atualiza as verbas trabalhistas no sistema",
    acceptsZipOnRetry: true,
  },
  {
    id: "preparar_fechamento",
    label: "Preparar para Fechamento",
    shortLabel: "Prep. Fech.",
    icon: ClipboardCheck,
    description: "Prepara todos os dados para o fechamento da folha",
  },
  {
    id: "fechar",
    label: "Fechar Folha",
    shortLabel: "Fechar",
    icon: Lock,
    description: "Realiza o fechamento da folha de pagamento",
    acceptsZipOnRetry: true,
  },
]

// Main visible steps (tab items) - excluding grouped analysis sub-actions
const TAB_STEPS = STEPS.filter((s) => !s.group)
const WORKFLOW_STORAGE_PREFIX = "previdenciario-workflow"
const STEP_EVENT_MAP: Partial<Record<WorkflowStep, string>> = {
  enviar_rubricas: "s1010",
  abrir_folha: "s1298",
  preparar_ambiente: "s3000",
  atualizar_verbas: "s1200-s2299",
  preparar_fechamento: "s1210",
  fechar: "s1299",
}

interface PersistedAttempt {
  id: number
  timestamp: string
  result: StepResultType
  files?: string[]
}

interface PersistedWorkflowState {
  runtimePrevidenciarioId: string | null
  results: Record<string, StepResultType>
  attempts: Record<string, PersistedAttempt[]>
  analysisEmpty: boolean
  activeTab: string
  analysisDownloadHistory: Record<
    "baixar_zip",
    { fileName: string; fileDateTime: string | null; downloadedAt: string } | null
  >
}

function extractResumoFromResponse(response: unknown): Resumo | null {
  if (!response || typeof response !== "object") return null
  const candidate = response as Record<string, unknown>

  if (candidate.resumo && typeof candidate.resumo === "object") {
    return candidate.resumo as Resumo
  }
  if (candidate.data && typeof candidate.data === "object") {
    const nested = candidate.data as Record<string, unknown>
    if (nested.resumo && typeof nested.resumo === "object") {
      return nested.resumo as Resumo
    }
    if (nested.eventos && nested.lote) {
      return nested as unknown as Resumo
    }
  }
  if (candidate.eventos && candidate.lote) {
    return candidate as unknown as Resumo
  }
  return null
}

function extractStructuredAnalysisState(response: unknown): { empty: boolean; message?: string } | null {
  if (!response || typeof response !== "object") return null

  const candidate = response as Record<string, unknown>
  const message = typeof candidate.message === "string" ? candidate.message : undefined
  const nestedData =
    candidate.data && typeof candidate.data === "object"
      ? (candidate.data as Record<string, unknown>)
      : null
  const resumoAnalise =
    nestedData?.resumoAnalise && typeof nestedData.resumoAnalise === "object"
      ? (nestedData.resumoAnalise as Record<string, unknown>)
      : null

  if (!resumoAnalise) return null

  return {
    empty: resumoAnalise.semDivergencias === true,
    message,
  }
}

function buildStepResultFromResponse(response: unknown): StepResultType {
  const resumo = extractResumoFromResponse(response)
  if (!resumo || !Array.isArray(resumo.eventos)) {
    const structuredAnalysisState = extractStructuredAnalysisState(response)
    return {
      status: structuredAnalysisState?.empty ? "empty" : "success",
      message:
        structuredAnalysisState?.message ??
        (structuredAnalysisState?.empty
          ? "Nenhuma oportunidade encontrada na analise"
          : "Etapa executada com sucesso."),
    }
  }

  const successCount = resumo.eventos.filter((e) => e.processamento.cdResposta === "201").length
  const warningCount = resumo.eventos.filter((e) => e.processamento.cdResposta === "202").length
  const errorCount = resumo.eventos.filter((e) => !["201", "202"].includes(e.processamento.cdResposta)).length

  let status: StepResultType["status"] = "success"
  if (resumo.eventos.length === 0) {
    status = "empty"
  } else if (errorCount > 0 && successCount === 0 && warningCount === 0) {
    status = "error"
  } else if (errorCount > 0 || warningCount > 0) {
    status = "warning"
  }

  return {
    status,
    data: resumo,
    successCount,
    errorCount,
    warningCount,
    totalCount: resumo.eventos.length,
    message: status === "empty" ? "Nenhuma oportunidade encontrada na analise" : undefined,
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return "Erro ao executar etapa."
}

function stepStatusFromWorkflowStatus(
  status: PrevidenciarioWorkflowResponse["steps"][WorkflowStep]["status"]
): StepResultType["status"] | null {
  if (status === "ready" || status === "locked") return null
  return status
}

function buildResultsFromWorkflow(workflow?: PrevidenciarioWorkflowResponse): Record<string, StepResultType> {
  if (!workflow) return {}

  return TAB_STEPS.reduce<Record<string, StepResultType>>((acc, step) => {
    const status = stepStatusFromWorkflowStatus(workflow.steps[step.id]?.status)
    if (!status) return acc

    acc[step.id] = {
      status,
      message:
        status === "empty"
          ? "Nenhuma oportunidade encontrada na analise"
          : workflow.steps[step.id]?.erro ?? "Etapa executada com sucesso.",
    }
    return acc
  }, {})
}

function buildAttemptsFromHistoricos(
  historicos: PrevidenciarioHistoricoResponse[] | undefined
): Record<string, Attempt[]> {
  if (!historicos?.length) return {}

  const attemptsByStep: Record<string, Attempt[]> = {}

  for (const historico of [...historicos].reverse()) {
    if (!TAB_STEPS.some((step) => step.id === historico.etapa)) continue

    const status = (() => {
      switch (historico.status) {
        case "sucesso":
          return "success" as const
        case "warning":
          return "warning" as const
        case "falha":
          return "error" as const
        case "vazio":
          return "empty" as const
        default:
          return null
      }
    })()

    if (!status) continue

    const currentAttempts = attemptsByStep[historico.etapa] ?? []
    currentAttempts.push({
      id: currentAttempts.length + 1,
      timestamp: new Date(historico.createdAt),
      result: {
        status,
        message:
          historico.erro ??
          (status === "empty"
            ? "Nenhuma oportunidade encontrada na analise"
            : "Etapa executada com sucesso."),
      },
      files: historico.arquivoNome ? [historico.arquivoNome] : [],
    })
    attemptsByStep[historico.etapa] = currentAttempts
  }

  return attemptsByStep
}

function parsePersistedState(rawValue: string | null): PersistedWorkflowState | null {
  if (!rawValue) return null

  try {
    const parsed = JSON.parse(rawValue) as PersistedWorkflowState
    return parsed
  } catch (_error) {
    return null
  }
}

function restoreAttempts(attempts: PersistedWorkflowState["attempts"]): Record<string, Attempt[]> {
  return Object.fromEntries(
    Object.entries(attempts).map(([stepId, stepAttempts]) => [
      stepId,
      stepAttempts.map((attempt) => ({
        ...attempt,
        timestamp: new Date(attempt.timestamp),
      })),
    ])
  )
}

interface WorkflowPanelProps {
  idPrevidenciario?: string
  idApuracaoCompetencia?: string
  onPrevidenciarioCreated?: (id: string) => void
}

export function WorkflowPanel({
  idPrevidenciario,
  idApuracaoCompetencia,
  onPrevidenciarioCreated,
}: WorkflowPanelProps) {
  const { toast } = useToast()
  const createPrevidenciarioMutation = useCreatePrevidenciario()
  const enviarZipMutation = useEnviarPrevidenciarioZip()
  const reenviarZipMutation = useReenviarPrevidenciarioZip()

  const filtrarS3000Mutation = useFiltrarS3000()
  const gerarXmlCorrigidoMutation = useGerarXmlCorrigido()
  const downloadPrevidenciarioZipMutation = useDownloadPrevidenciarioZip()
  const [runtimePrevidenciarioId, setRuntimePrevidenciarioId] = useState<string | null>(null)
  const [results, setResults] = useState<Record<string, StepResultType>>({})
  const [attempts, setAttempts] = useState<Record<string, Attempt[]>>({})
  const [currentStep, setCurrentStep] = useState<WorkflowStep | null>(null)
  const [currentDownloadAction, setCurrentDownloadAction] = useState<"baixar_zip" | null>(null)
  const [analysisEmpty, setAnalysisEmpty] = useState(false)
  const [activeTab, setActiveTab] = useState<string>("limpar_xmls")
  const [zipFiles, setZipFiles] = useState<Record<string, File[]>>({})
  const [selectedAnalysisFiles, setSelectedAnalysisFiles] = useState<Record<"baixar_zip", string>>({
    baixar_zip: "",
  })
  const [analysisDownloadHistory, setAnalysisDownloadHistory] = useState<
    Record<
      "baixar_zip",
      { fileName: string; fileDateTime: string | null; downloadedAt: Date } | null
    >
  >({
    baixar_zip: null,
  })
  const effectivePrevidenciarioId =
    (runtimePrevidenciarioId?.trim() || idPrevidenciario?.trim()) ?? null

  const workflowStorageKey = useMemo(() => {
    if (idApuracaoCompetencia?.trim()) {
      return `${WORKFLOW_STORAGE_PREFIX}:${idApuracaoCompetencia.trim()}`
    }

    if (effectivePrevidenciarioId?.trim()) {
      return `${WORKFLOW_STORAGE_PREFIX}:${effectivePrevidenciarioId.trim()}`
    }

    return null
  }, [effectivePrevidenciarioId, idApuracaoCompetencia])

  const { data: previdenciarioDetalhes } = useFetchPrevidenciarioDetalhesById(
    effectivePrevidenciarioId || undefined
  )

  useEffect(() => {
    if (!workflowStorageKey || typeof window === "undefined") {
      setRuntimePrevidenciarioId(idPrevidenciario ?? null)
      setResults({})
      setAttempts({})
      setAnalysisEmpty(false)
      setActiveTab("limpar_xmls")
      setAnalysisDownloadHistory({ baixar_zip: null })
      return
    }

    const persisted = parsePersistedState(window.localStorage.getItem(workflowStorageKey))
    if (!persisted) {
      setRuntimePrevidenciarioId(idPrevidenciario ?? null)
      setResults({})
      setAttempts({})
      setAnalysisEmpty(false)
      setActiveTab("limpar_xmls")
      setAnalysisDownloadHistory({ baixar_zip: null })
      return
    }

    setRuntimePrevidenciarioId(
      persisted.runtimePrevidenciarioId?.trim() || idPrevidenciario?.trim() || null
    )
    setResults(persisted.results ?? {})
    setAttempts(restoreAttempts(persisted.attempts ?? {}))
    setAnalysisEmpty(Boolean(persisted.analysisEmpty))
    setActiveTab(persisted.activeTab || "limpar_xmls")
    setAnalysisDownloadHistory({
      baixar_zip: persisted.analysisDownloadHistory?.baixar_zip
        ? {
            ...persisted.analysisDownloadHistory.baixar_zip,
            downloadedAt: new Date(persisted.analysisDownloadHistory.baixar_zip.downloadedAt),
          }
        : null,
    })
  }, [workflowStorageKey, idPrevidenciario])

  useEffect(() => {
    if (!workflowStorageKey || typeof window === "undefined") return

    const persisted: PersistedWorkflowState = {
      runtimePrevidenciarioId,
      results,
      attempts: Object.fromEntries(
        Object.entries(attempts).map(([stepId, stepAttempts]) => [
          stepId,
          stepAttempts.map((attempt) => ({
            ...attempt,
            timestamp: attempt.timestamp.toISOString(),
          })),
        ])
      ),
      analysisEmpty,
      activeTab,
      analysisDownloadHistory: {
        baixar_zip: analysisDownloadHistory.baixar_zip
          ? {
              ...analysisDownloadHistory.baixar_zip,
              downloadedAt: analysisDownloadHistory.baixar_zip.downloadedAt.toISOString(),
            }
          : null,
      },
    }

    window.localStorage.setItem(workflowStorageKey, JSON.stringify(persisted))
  }, [
    workflowStorageKey,
    runtimePrevidenciarioId,
    results,
    attempts,
    analysisEmpty,
    activeTab,
    analysisDownloadHistory,
  ])

  useEffect(() => {
    if (!previdenciarioDetalhes) return

    const workflow = previdenciarioDetalhes.workflow
    const historicos = previdenciarioDetalhes.historicos

    if (previdenciarioDetalhes.id && !runtimePrevidenciarioId) {
      setRuntimePrevidenciarioId(previdenciarioDetalhes.id)
    }

    if (workflow) {
      setResults((prev) => {
        const next = { ...buildResultsFromWorkflow(workflow), ...prev }

        for (const step of TAB_STEPS) {
          const workflowStatus = stepStatusFromWorkflowStatus(workflow.steps[step.id]?.status)
          if (!workflowStatus) continue

          next[step.id] = {
            ...next[step.id],
            status: workflowStatus,
            message:
              prev[step.id]?.message ??
              workflow.steps[step.id]?.erro ??
              (workflowStatus === "empty"
                ? "Nenhuma oportunidade encontrada na analise"
                : "Etapa executada com sucesso."),
          }
        }

        return next
      })

      setAnalysisEmpty(workflow.analysisEmpty)

      if (workflow.currentStep) {
        const currentWorkflowStep = workflow.currentStep
        setActiveTab((prev) => (prev === "limpar_xmls" ? currentWorkflowStep : prev))
      }
    }

    if (historicos?.length) {
      setAttempts((prev) => {
        const next = { ...prev }
        const fallbackAttempts = buildAttemptsFromHistoricos(historicos)

        for (const [stepId, stepAttempts] of Object.entries(fallbackAttempts)) {
          const existingAttempts = next[stepId] ?? []
          const mergedAttempts = [...existingAttempts]

          for (const stepAttempt of stepAttempts) {
            const alreadyExists = existingAttempts.some((existingAttempt) => {
              const sameTimestamp = existingAttempt.timestamp.getTime() === stepAttempt.timestamp.getTime()
              const sameStatus = existingAttempt.result.status === stepAttempt.result.status
              const sameMessage = existingAttempt.result.message === stepAttempt.result.message
              const sameFiles = JSON.stringify(existingAttempt.files ?? []) === JSON.stringify(stepAttempt.files ?? [])
              return sameTimestamp && sameStatus && sameMessage && sameFiles
            })

            if (!alreadyExists) {
              mergedAttempts.push({
                ...stepAttempt,
                id: mergedAttempts.length + 1,
              })
            }
          }

          next[stepId] = mergedAttempts.sort(
            (left, right) => left.timestamp.getTime() - right.timestamp.getTime()
          )
        }

        return next
      })
    }
  }, [previdenciarioDetalhes, runtimePrevidenciarioId])

  const isStepUnlocked = useCallback(
    (stepId: WorkflowStep): boolean => {
      console.log(`stepId`,stepId)
      switch (stepId) {
        case "limpar_xmls":
          return true
        case "fazer_analise":
          return results.limpar_xmls?.status === "success"
        case "enviar_rubricas":
          return (
            results.fazer_analise?.status === "success" ||
            results.fazer_analise?.status === "warning" ||
            results.fazer_analise?.status === "error" ||
            results.fazer_analise?.status === "empty"
          )
        case "abrir_folha":
          return (
            (results.enviar_rubricas?.status === "success" ||
              results.enviar_rubricas?.status === "warning" ||
              results.enviar_rubricas?.status === "error") ||
            analysisEmpty
          )
        case "preparar_ambiente":
          return results.abrir_folha?.status === "success"
        case "atualizar_verbas":
          return results.preparar_ambiente?.status === "success"
        case "preparar_fechamento":
          return results.atualizar_verbas?.status === "success" || results.atualizar_verbas?.status === "warning"
        case "fechar":
          return results.preparar_fechamento?.status === "success" || results.preparar_fechamento?.status === "warning"
        default:
          return false
      }
    },
    [results, analysisEmpty]
  )

  const analysisActionsVisible = useMemo(() => {
    return (
      (results.fazer_analise?.status === "success" ||
        results.fazer_analise?.status === "warning" ||
        results.fazer_analise?.status === "error") &&
      !analysisEmpty
    )
  }, [results, analysisEmpty])

  const certInfo = readStoredA1Certificate()
  const hasValidCertificate = Boolean(certInfo?.certFile && certInfo?.certPassword?.trim())

  const formatDownloadDateTime = useCallback((date: Date): string => {
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }, [])

  const analysisFileOptionsWithDate = useMemo(() => {
    const eventos = results.fazer_analise?.data?.eventos || []
    const fallbackDate = results.fazer_analise?.data?.lote?.dhRecepcao || null

    return ANALYSIS_FILE_OPTIONS.map((option, index) => ({
      ...option,
      fileDateTime: eventos[index]?.processamento?.dhProcessamento || fallbackDate,
    }))
  }, [results.fazer_analise?.data])

  const getFormattedDateFromSource = useCallback(
    (rawDate: string | null | undefined): string => {
      if (!rawDate) return "Data/hora indisponivel"
      const parsed = new Date(rawDate)
      if (Number.isNaN(parsed.getTime())) return "Data/hora indisponivel"
      return formatDownloadDateTime(parsed)
    },
    [formatDownloadDateTime]
  )

  const executeAnalysisDownload = useCallback(
    async (actionId: "baixar_zip") => {
      const selectedFileValue = selectedAnalysisFiles[actionId]
      if (!selectedFileValue) return

      const selectedFileInfo = analysisFileOptionsWithDate.find(
        (option) => option.value === selectedFileValue
      )
      if (!selectedFileInfo) return

      const effectivePrevidenciarioId = runtimePrevidenciarioId ?? idPrevidenciario
      if (!effectivePrevidenciarioId?.trim()) {
        toast({
          variant: "destructive",
          description: "ID previdenciario nao encontrado para baixar os arquivos."
        })
        return
      }

      setCurrentDownloadAction(actionId)
      try {
        const response = await downloadPrevidenciarioZipMutation.mutateAsync({
          idPrevidenciario: effectivePrevidenciarioId,
          evento: selectedFileValue,
          labelDownload: selectedFileInfo.label
        })

        if (isDownloadFileResult(response)) {
          triggerDownloadFile(response)
        }

        setAnalysisDownloadHistory((prev) => ({
          ...prev,
          [actionId]: {
            fileName: selectedFileInfo.label,
            fileDateTime: selectedFileInfo.fileDateTime,
            downloadedAt: new Date(),
          },
        }))
      } catch (error) {
        toast({
          variant: "destructive",
          description: getErrorMessage(error)
        })
      } finally {
        setCurrentDownloadAction(null)
      }
    },
    [
      selectedAnalysisFiles,
      analysisFileOptionsWithDate,
      runtimePrevidenciarioId,
      idPrevidenciario,
      downloadPrevidenciarioZipMutation,
      toast
    ]
  )

  const ensurePrevidenciarioId = useCallback(async (): Promise<string> => {
    const currentId = runtimePrevidenciarioId?.trim() || idPrevidenciario?.trim()
    console.log("Ensuring previdenciario ID. Current:", currentId, "Runtime:", runtimePrevidenciarioId, "Prop:", idPrevidenciario)
    if (currentId) {
      return currentId
    }

    if (!idApuracaoCompetencia) {
      throw new Error("Crie a competência antes de executar o processamento.")
    }

    const createdPrevidenciario = await createPrevidenciarioMutation.mutateAsync({
      id_apuracao_competencia: idApuracaoCompetencia,
    })

    setRuntimePrevidenciarioId(createdPrevidenciario.id)
    onPrevidenciarioCreated?.(createdPrevidenciario.id)
    return createdPrevidenciario.id
  }, [
    runtimePrevidenciarioId,
    idPrevidenciario,
    idApuracaoCompetencia,
    createPrevidenciarioMutation,
    onPrevidenciarioCreated,
  ])

  const executeStep = useCallback(
    async (stepId: WorkflowStep) => {
      const files = zipFiles[stepId] || []
      setCurrentStep(stepId)
      setResults((prev) => ({
        ...prev,
        [stepId]: { status: "loading" as const },
      }))

      try {
        let effectivePrevidenciarioId = runtimePrevidenciarioId ?? idPrevidenciario

        let response: unknown

        switch (stepId) {
          case "limpar_xmls": {
            const file = files[0]
            if (!file) throw new Error("Envie um arquivo ZIP para limpar XMLs.")
            effectivePrevidenciarioId = await ensurePrevidenciarioId()
            response = await filtrarS3000Mutation.mutateAsync({
              file,
              idPrevidenciario: effectivePrevidenciarioId,
            })
            if (isDownloadFileResult(response)) {
              triggerDownloadFile(response)
            }
            break
          }
          case "fazer_analise": {
            const file = files[0]
            if (!file) throw new Error("Envie um arquivo ZIP para fazer analise.")
            effectivePrevidenciarioId = await ensurePrevidenciarioId()

            response = await gerarXmlCorrigidoMutation.mutateAsync({
              file,
              idPrevidenciario: effectivePrevidenciarioId,
              upgradeLegacyLayout: true,
              tpAmb: "1",
              responseFormat: "json",
            })
            break
          }
          default: {
            if (!certInfo?.certFile || !certInfo?.certPassword?.trim()) {
              throw new Error("Adicione certificado A1 e senha antes de enviar ou reenviar eventos.")
            }
            effectivePrevidenciarioId = await ensurePrevidenciarioId()
            const evento = STEP_EVENT_MAP[stepId]
            const hasRetryFile = files.length > 0
            const isRetryAttempt = Boolean(results[stepId] && results[stepId].status !== "idle")

            if (hasRetryFile || isRetryAttempt) {
              response = await reenviarZipMutation.mutateAsync({
                idPrevidenciario: effectivePrevidenciarioId,
                evento,
                file: files[0],
                certFile: certInfo?.certFile,
                certPassword: certInfo?.certPassword,
              })
            } else {
              response = await enviarZipMutation.mutateAsync({
                idPrevidenciario: effectivePrevidenciarioId,
                evento,
                certFile: certInfo?.certFile,
                certPassword: certInfo?.certPassword,
              })
            }
            break
          }
        }

        const newResult = buildStepResultFromResponse(response)
        if (stepId === "fazer_analise") {
          setAnalysisEmpty(newResult.status === "empty")
        }

        setResults((prev) => ({ ...prev, [stepId]: newResult }))
        setAttempts((prev) => ({
          ...prev,
          [stepId]: [
            ...(prev[stepId] || []),
            {
              id: (prev[stepId]?.length || 0) + 1,
              timestamp: new Date(),
              result: newResult,
              files: files.map((f) => f.name),
            },
          ],
        }))
      } catch (error) {
        const message = getErrorMessage(error)
        const failedResult: StepResultType = { status: "error", message }
        setResults((prev) => ({ ...prev, [stepId]: failedResult }))
        setAttempts((prev) => ({
          ...prev,
          [stepId]: [
            ...(prev[stepId] || []),
            {
              id: (prev[stepId]?.length || 0) + 1,
              timestamp: new Date(),
              result: failedResult,
              files: files.map((f) => f.name),
            },
          ],
        }))
        toast({ variant: "destructive", description: message })
      } finally {
        setCurrentStep(null)
        setZipFiles((prev) => ({ ...prev, [stepId]: [] }))
      }
    },
    [
      zipFiles,
      idPrevidenciario,
      idApuracaoCompetencia,
      runtimePrevidenciarioId,
      ensurePrevidenciarioId,
      filtrarS3000Mutation,
      gerarXmlCorrigidoMutation,
      reenviarZipMutation,
      enviarZipMutation,
      toast,
      results,
      certInfo,
    ]
  )

  const getStepStatus = (stepId: WorkflowStep): StepResultType["status"] | "locked" | "ready" => {
    if (currentStep === stepId) return "loading"
    const result = results[stepId]
    if (result && result.status !== "idle") return result.status
    if (!isStepUnlocked(stepId)) return "locked"
    return "ready"
  }

  const stepHasErrors = useCallback(
    (stepId: WorkflowStep): boolean => {
      const result = results[stepId]
      if (!result) return false
      if (typeof result.errorCount === "number") return result.errorCount > 0
      return result.status === "error"
    },
    [results]
  )

  const getStatusIndicator = (status: StepResultType["status"] | "locked" | "ready") => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-3.5 w-3.5 text-success" />
      case "error":
        return <XCircle className="h-3.5 w-3.5 text-destructive" />
      case "warning":
        return <AlertTriangle className="h-3.5 w-3.5 text-warning" />
      case "empty":
        return <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
      case "loading":
        return <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
      default:
        return null
    }
  }

  const completedCount = Object.values(results).filter(
    (r) => r.status === "success" || r.status === "warning" || r.status === "empty"
  ).length

  const renderStepContent = (step: StepConfig) => {
    const status = getStepStatus(step.id)
    const result = results[step.id]
    const isLoading = currentStep === step.id
    const isCompleted = result && result.status !== "idle" && result.status !== "loading"
    const stepAttempts = attempts[step.id] || []
    const files = zipFiles[step.id] || []
    const Icon = step.icon
    const hasErrors = stepHasErrors(step.id)
    const showZipUpload = step.acceptsZip || hasErrors || (step.acceptsZipOnRetry && isCompleted)
    const requiresCertificate =
      step.id !== "limpar_xmls" && step.id !== "fazer_analise"
    const canReuseSavedZip =
      isCompleted &&
      requiresCertificate &&
      files.length === 0
    const actionDisabled =
      isLoading ||
      (requiresCertificate && !(runtimePrevidenciarioId ?? idPrevidenciario)) ||
      (requiresCertificate && !hasValidCertificate) ||
      (currentStep !== null && currentStep !== step.id)
    const actionLabel = isLoading ? "Processando..." : isCompleted ? "Reexecutar" : "Executar"
    const actionIcon = isLoading ? (
      <Loader2 className="h-4 w-4 animate-spin mr-2" />
    ) : isCompleted ? (
      <RefreshCw className="h-4 w-4 mr-2" />
    ) : null
    const renderActionButton = (onClick?: () => void) => (
      <Button
        className="w-full"
        size="lg"
        variant={isCompleted ? "outline" : "default"}
        disabled={actionDisabled}
        onClick={onClick}
      >
        {actionIcon}
        {actionLabel}
      </Button>
    )

    return (
      <div className="space-y-4">
        {/* Step header */}
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all",
              status === "success" && "bg-success/10 text-success",
              status === "error" && "bg-destructive/10 text-destructive",
              status === "warning" && "bg-warning/10 text-warning",
              status === "loading" && "bg-primary/10 text-primary",
              (status === "ready" || status === "locked") && "bg-accent text-muted-foreground"
            )}
          >
            {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground break-words">{step.label}</h2>
            <p className="text-sm text-muted-foreground break-words">{step.description}</p>
          </div>
          {isCompleted && (
            <div className="shrink-0">{getStatusIndicator(result.status)}</div>
          )}
        </div>

        {/* Zip upload */}
        {showZipUpload && (
          <ZipUpload
            files={files}
            onFilesChange={(newFiles) =>
              setZipFiles((prev) => ({ ...prev, [step.id]: newFiles }))
            }
            label={
              step.id === "limpar_xmls"
                ? "Envie o ZIP com os XMLs para limpar"
                : step.id === "fazer_analise"
                ? "Envie o ZIP para analise"
                : hasErrors && isCompleted
                ? "Opcional: envie ZIP corrigido para reexecutar. Sem ZIP, o sistema usa os arquivos salvos."
                : "Opcional: envie ZIP para reexecutar esta etapa"
            }
          />
        )}

        {/* Action button */}
        {canReuseSavedZip ? (
          <ButtonConfirm
            disabled={actionDisabled}
            trigger={renderActionButton()}
            confirmText="Nenhum ZIP foi anexado. Deseja reenviar os mesmos arquivos salvos desta etapa?"
            confirmBtnText="Sim, reenviar"
            cancelBtnText="Vou anexar ZIP"
            confirmBtnVariant="default"
            onConfirm={() => executeStep(step.id)}
            onCancel={() =>
              toast({
                description: "Se quiser escolher outro arquivo, anexe 1 arquivo ZIP antes de reenviar.",
              })
            }
          />
        ) : (
          renderActionButton(() => executeStep(step.id))
        )}

        {step.id !== "limpar_xmls" && step.id !== "fazer_analise" && !hasValidCertificate && (
          <p className="text-xs text-muted-foreground break-words">
            Adicione certificado A1 e senha para enviar, assinar e consultar os eventos.
          </p>
        )}

        {/* Status message for empty/download */}
        {isCompleted && result.status === "empty" && (
          <div className="flex items-center gap-2 rounded-lg bg-muted p-3">
            <Inbox className="h-4 w-4 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground break-words">{result.message}</p>
          </div>
        )}

        {/* Step result */}
        {isCompleted && result.data && (
          <StepResult data={result.data} stepName={step.label} />
        )}

        {/* Attempt history */}
        {stepAttempts.length > 1 && (
          <AttemptHistory attempts={stepAttempts.slice(0, -1).reverse()} />
        )}
      </div>
    )
  }

  const renderAnalysisActions = () => {
    if (!analysisActionsVisible) return null

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 px-1">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs font-medium text-muted-foreground">Acoes da Analise</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1">
          {ANALYSIS_DOWNLOAD_ACTIONS.map((action) => {
            const Icon = action.icon
            const selectedFile = selectedAnalysisFiles[action.id]
            const lastDownload = analysisDownloadHistory[action.id]
            const selectedFileInfo = analysisFileOptionsWithDate.find(
              (option) => option.value === selectedFile
            )
            const isLoading = currentDownloadAction === action.id
            const isBusy = currentDownloadAction !== null && currentDownloadAction !== action.id

            return (
              <div
                key={action.id}
                className={cn(
                  "rounded-xl border p-4 transition-all",
                  isLoading ? "border-primary/50 bg-primary/5" : "border-border bg-card hover:border-primary/30"
                )}
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="block text-xs font-medium text-foreground break-words">{action.label}</span>
                    <p className="text-xs text-muted-foreground break-words">{action.description}</p>
                  </div>
                </div>

                <div className="mt-3 space-y-1.5">
                  <label htmlFor={`analysis-download-${action.id}`} className="text-xs font-medium text-muted-foreground">
                    Arquivo disponivel
                  </label>
                  <select
                    id={`analysis-download-${action.id}`}
                    value={selectedFile}
                    onChange={(event) =>
                      setSelectedAnalysisFiles((prev) => ({ ...prev, [action.id]: event.target.value }))
                    }
                    className={cn(
                      "w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground",
                      "focus:outline-none focus:ring-2 focus:ring-primary/30"
                    )}
                  >
                    <option value="">Selecione o arquivo</option>
                    {analysisFileOptionsWithDate.map((fileOption) => (
                      <option key={fileOption.value} value={fileOption.value}>
                        {fileOption.label}
                      </option>
                    ))}
                  </select>
                  {selectedFileInfo && (
                    <p className="text-xs text-muted-foreground break-words">
                      Data/Hora do arquivo: {getFormattedDateFromSource(selectedFileInfo.fileDateTime)}
                    </p>
                  )}
                </div>

                <Button
                  className="w-full mt-3"
                  size="sm"
                  variant="outline"
                  disabled={isLoading || isBusy || !selectedFile || !(runtimePrevidenciarioId ?? idPrevidenciario)}
                  onClick={() => executeAnalysisDownload(action.id)}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      Baixando...
                    </>
                  ) : (
                    action.label
                  )}
                </Button>

                {lastDownload ? (
                  <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                    <p className="break-words">
                      {lastDownload.fileName} | Data/Hora do arquivo:{" "}
                      {getFormattedDateFromSource(lastDownload.fileDateTime)}
                    </p>
                    <p className="break-words">Download realizado em {formatDownloadDateTime(lastDownload.downloadedAt)}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground break-words">
                    Nenhum download realizado ainda.
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Find the "fazer_analise" tab to embed analysis actions
  const renderTabContent = (step: StepConfig) => {
    if (step.id === "fazer_analise") {
      return (
        <div className="space-y-6">
          {renderStepContent(step)}
          {renderAnalysisActions()}
        </div>
      )
    }
    return renderStepContent(step)
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="mx-auto max-w-4xl px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-foreground break-words">Processamento de Folha</h1>
              <p className="text-xs text-muted-foreground break-words">eSocial - Fluxo de Trabalho</p>
            </div>
            <div className="flex items-center gap-3 min-w-0">
              <Badge variant="outline" className="border-success/30 text-success bg-success/5 max-w-full whitespace-normal break-words text-[11px] sm:text-xs">
                <div className="h-1.5 w-1.5 rounded-full bg-success animate-pulse mr-1" />
                {completedCount} de {TAB_STEPS.length} etapas
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs Content */}
      <main className="mx-auto max-w-4xl px-4 py-6">


        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full h-auto flex-wrap gap-1 bg-card border border-border p-1.5 rounded-xl">
            {TAB_STEPS.map((step, index) => {
              const status = getStepStatus(step.id)
              const isLocked = status === "locked"
              const Icon = step.icon

              return (
                <TabsTrigger
                  key={step.id}
                  value={step.id}
                  disabled={isLocked}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-all",
                    "data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none",
                    isLocked && "opacity-40 cursor-not-allowed"
                  )}
                >
                  <span className="hidden sm:inline-flex items-center gap-1.5">
                    {status !== "ready" && status !== "locked" ? (
                      getStatusIndicator(status)
                    ) : (
                      <span className="text-xs font-mono text-muted-foreground">{index + 1}</span>
                    )}
                    {step.shortLabel}
                  </span>
                  <span className="sm:hidden inline-flex items-center gap-1">
                    {status !== "ready" && status !== "locked" ? (
                      getStatusIndicator(status)
                    ) : (
                      <Icon className="h-3.5 w-3.5" />
                    )}
                  </span>
                </TabsTrigger>
              )
            })}
          </TabsList>

          {TAB_STEPS.map((step) => (
            <TabsContent key={step.id} value={step.id} className="mt-4">
              <div className="rounded-xl border border-border bg-card p-5">
                {renderTabContent(step)}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* All done message */}
        {results.fechar?.status === "success" && (
          <div className="mt-6 rounded-xl border border-success/30 bg-success/5 p-6 text-center animate-in fade-in zoom-in-95 duration-500">
            <CheckCircle2 className="h-10 w-10 text-success mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-foreground break-words">Folha Fechada com Sucesso</h2>
            <p className="text-sm text-muted-foreground mt-1 break-words">
              Todas as etapas do fluxo foram concluidas.
            </p>
          </div>
        )}
      </main>
    </div>
  )
}
